import {Injectable} from '@angular/core';
import moment from 'moment';
import {CookieService} from 'ngx-cookie';
import {Observable, of} from 'rxjs';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {AppConfigService} from '../../services/config.service';
import {UserService} from '../../services/user.service';
import {WindowRefService} from '../../services/window-ref.service';

const SIM_APP_COOKIE_EXPIRATION_TIMEOUT_SEC = 30;

@Injectable()
export class LinkToSimulatorService {

    public readonly isEnabledInConfig: boolean = !!(
        this.appConfigService.appConfig.simulatorAppConfig
        && (
            this.appConfigService.appConfig.simulatorAppConfig.showSimulatorAppLink
            || this.appConfigService.appConfig.simulatorAppConfig.isSimulatorApp
        )
    );

    public readonly linkUrl: string = this.isEnabledInConfig
        ? this.appConfigService.appConfig.simulatorAppConfig.simulatorAppLinkUrl
        : '';

    public readonly password: string = this.isEnabledInConfig
        ? this.appConfigService.appConfig.simulatorAppConfig.simulatorAppPassword
        : '';

    private readonly isLinkAvailableInConfig: boolean = !!(
        this.linkUrl
        && !this.appConfigService.appConfig.simulatorAppConfig.isSimulatorApp
        && this.appConfigService.appConfig.simulatorAppConfig.showSimulatorAppLink
    );

    private readonly cookiesDomain: string = this.isEnabledInConfig
        ? this.appConfigService.appConfig.simulatorAppConfig.simulatorAppCookiesDomain
        : '';

    private readonly simulatorAppCookieLoginKey = this.isEnabledInConfig
        ? this.appConfigService.appConfig.simulatorAppConfig.simulatorAppCookieLoginKey
        : '';

    constructor(
        private cookieService: CookieService,
        private userService: UserService,
        private windowRefService: WindowRefService,
        private appConfigService: AppConfigService
    ) {}

    public getLinkAvailabilityStream(): Observable<boolean> {
        return this.isLinkAvailableInConfig
            ? this.userService.user$.pipe(
                map(user => !!user),
                distinctUntilChanged()
            )
            : of(false);
    }

    public navigateToSimulator(): void {
        const user = this.userService.user;

        if (user && this.isLinkAvailableInConfig) {
            try {
                this.cookieService.put(
                    this.simulatorAppCookieLoginKey,
                    String(user.login),
                    {
                        domain:  this.cookiesDomain,
                        expires: moment().add(SIM_APP_COOKIE_EXPIRATION_TIMEOUT_SEC, 'seconds').toDate()
                    }
                );
            } catch (e) {
                console.warn('Error while passing auth data to Simulator app', e);
            }

            this.windowRefService.nativeWindow.open(this.linkUrl, '_blank');
        }
    }

    public readAndRemoveSimulatorLoginCookie(): string | undefined {
        let loginValue: string;

        if (this.isEnabledInConfig) {
            const hasLoginValue = this.cookieService.hasKey(this.simulatorAppCookieLoginKey);

            if (hasLoginValue) {
                loginValue = this.cookieService.get(this.simulatorAppCookieLoginKey);
                loginValue = String(loginValue).trim();

                this.cookieService.remove(
                    this.simulatorAppCookieLoginKey,
                    {
                        domain: this.cookiesDomain
                    }
                );
            }
        }

        return loginValue || undefined;
    }

}
